import {useEffect, useMemo, useState, type CSSProperties} from "react";
import {Button} from "@/primitives/Button";
import {FA6Icon} from "@/primitives/FA6Icon";
import {Checkbox} from "@/primitives/Checkbox";
import {InputGroup} from "@/primitives/InputGroup";
import {Label} from "@/primitives/Label";
import {Select} from "@/primitives/Select";
import {api} from "@/shared/api";
import {Notify} from "@/shared/ui/notify";
import {formatTime, Temporal} from "@/shared/utils/datefunctions";

/* Preselected warning hour while nothing is stored yet — mirrors the legacy select default. */
const DEFAULT_WARNING_HOUR = 18;

const HOUR_SELECT_ID = "runningTimerWarningAfterHour";

/* InputGroup.Root spans the full width by default; in this row it may only be as wide as its cells. */
const INLINE_GROUP_STYLE: CSSProperties = {width: "auto"};

interface EmailSettingsPanelProps {
	newsletter: boolean;
	runningTimerWarningAfterHour: number | null;
	workingTimeRequestStatusEmail: boolean;
	workingTimeRequestsEnabled: boolean;
}

export function EmailSettingsPanel({
	newsletter: initialNewsletter,
	runningTimerWarningAfterHour: initialWarningHour,
	workingTimeRequestStatusEmail: initialRequestStatusEmail,
	workingTimeRequestsEnabled,
}: EmailSettingsPanelProps) {
	const [savedValues, setSavedValues] = useState({
		newsletter: initialNewsletter,
		runningTimerWarning: initialWarningHour != null,
		warningHour: initialWarningHour ?? DEFAULT_WARNING_HOUR,
		requestStatusEmail: initialRequestStatusEmail,
	});
	const [newsletter, setNewsletter] = useState(savedValues.newsletter);
	const [runningTimerWarning, setRunningTimerWarning] = useState(savedValues.runningTimerWarning);
	const [warningHour, setWarningHour] = useState(savedValues.warningHour);
	const [requestStatusEmail, setRequestStatusEmail] = useState(savedValues.requestStatusEmail);
	const [saving, setSaving] = useState(false);

	const unsavedChanges =
		newsletter !== savedValues.newsletter
		|| runningTimerWarning !== savedValues.runningTimerWarning
		|| warningHour !== savedValues.warningHour
		|| requestStatusEmail !== savedValues.requestStatusEmail;

	useEffect(() => {
		if (!unsavedChanges) {
			return;
		}
		const warn = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			/* Legacy trigger (Chrome/Edge < 119); modern browsers show their own generic text anyway. */
			event.returnValue = "You will lose all changes made since your last save";
		};
		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, [unsavedChanges]);

	const hourOptions = useMemo(
		() =>
			Array.from({length: 24}, (_, hour) => ({
				label: formatTime(Temporal.PlainTime.from({hour})),
				value: hour,
			})),
		[],
	);

	/*
	 * Not every language needs a suffix ("after 18:00" reads fine without one), and a language
	 * file may not carry the key at all — the per-locale `messages` bundle has no key-level
	 * fallback to the base file. Both cases must skip the addon, or it renders as a blank box.
	 */
	const hourSuffix = messages["emailsettings.runningTimerWarningAfterHour.suffix"]?.trim();

	/* black while the reminder is on, the addon's muted grey while disabled */
	const textClassName = runningTimerWarning ? "text-default" : "text-grey-600";

	const save = () => {
		/* Snapshot so edits made while the request is in flight stay marked as unsaved. */
		const values = {newsletter, runningTimerWarning, warningHour, requestStatusEmail};
		setSaving(true);
		api("saveEmailSettings.json")
			.post({
				newsletter: values.newsletter,
				runningTimerWarning: values.runningTimerWarning,
				runningTimerWarningAfterHour: values.warningHour,
				workingTimeRequestStatusEmail: values.requestStatusEmail,
			})
			.done(() => {
				setSavedValues(values);
				Notify.success(messages["emailSettings.changed"]);
			})
			.always(() => setSaving(false));
	};

	return (
		<div className="panel panel-flat">
			<div className="panel-heading">
				<h5 className="panel-title">{messages["emailsettings.title"]}</h5>
			</div>
			<div className="panel-body form-horizontal">
				<fieldset className="content-group">
					<div className="mb-20">
						<Label>
							<Checkbox
								checked={newsletter}
								onCheckedChange={(checked) => setNewsletter(checked)}
							/>
							{messages["emailsettings.newsletter.label"]}
						</Label>
					</div>

					<div className="form-group">
						{/*
						 * A flex row puts the group right beside the label text. The checkbox label must
						 * stay a sibling — enclosing the group would squeeze the checkbox into the
						 * label's own inline row. Centred, not stretched: once the label wraps at smaller
						 * widths, a stretching row would grow the group's addons while the select keeps
						 * its fixed control height.
						 */}
						<div className="col-lg-12 display-flex-items-center">
							<Label className="mr-20">
								<Checkbox
									checked={runningTimerWarning}
									onCheckedChange={(checked) => setRunningTimerWarning(checked)}
								/>
								{messages["emailsettings.runningTimerWarning.label"]}
							</Label>
							{/*
							 * Always rendered and only disabled while the reminder is off — hiding it
							 * would change the row height and shift the rest of the panel.
							 */}
							<InputGroup.Root style={INLINE_GROUP_STYLE}>
								<InputGroup.Addon
									className={textClassName}
									render={<label htmlFor={HOUR_SELECT_ID}/>}
								>
									{messages["emailsettings.runningTimerWarningAfterHour.label"]}
								</InputGroup.Addon>
								<Select.Root
									items={hourOptions}
									value={warningHour}
									onValueChange={(value) => setWarningHour(value)}
									disabled={!runningTimerWarning}
								>
									<Select.Trigger id={HOUR_SELECT_ID} className={textClassName}/>
									<Select.Items/>
								</Select.Root>
								{hourSuffix && (
									<InputGroup.Addon className={textClassName}>{hourSuffix}</InputGroup.Addon>
								)}
							</InputGroup.Root>
						</div>
					</div>

					{workingTimeRequestsEnabled && (
						<Label>
							<Checkbox
								checked={requestStatusEmail}
								onCheckedChange={(checked) => setRequestStatusEmail(checked)}
							/>
							{messages["emailsettings.workingTimeRequestStatusEmail.label"]}
						</Label>
					)}
				</fieldset>

				<div className="text-right">
					<Button onClick={save} disabled={saving}>
						<FA6Icon icon="floppy-disk" variant="regular" fixedWidth className="mr-5"/>
						{messages["dialog.save"]}
					</Button>
				</div>
			</div>
		</div>
	);
}
